/**
 * Strategy 4 — Weekly NIFTY AI Debit Spread
 *
 * Layout:
 *  ┌─ Header ─────────────────────────────────────────────────────────────────┐
 *  │  Strategy 4 title · collapsible · ⓘ About · Generate button             │
 *  └──────────────────────────────────────────────────────────────────────────┘
 *  [About panel — inline, slides in below header when open]
 *
 *  Row 1: [Prediction (200px)] | [Recommended Setup (1fr)] | [Upcoming Events (220px)]
 *  Row 2: [AI Reasoning] | [Macro Backdrop]
 *  Row 3: [Signal Weights (left)] | [Signal Snapshot sorted by weight (right)]
 *  Row 4: Performance History chart + collapsible table
 *  Row 5: Record Outcome (collapsible)
 */

import React, { useState, useEffect, useCallback, useContext } from 'react';
import { HeaderActionsContext } from './strategyCardContext';
import { STRATEGY_INFO } from '../../data/strategyInfo';
import {
  generateStrategy4Prediction,
  getStrategy4Current,
  getStrategy4History,
  postStrategy4Outcome,
  getStrategy4Events,
  addStrategy4Event,
  deleteStrategy4Event,
} from '../../api/client';

// ── CardSection — collapsible body section inside expanded strategy card ──────
function CardSection({ label, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ borderTop: '.5px solid var(--hair)' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          width: '100%', padding: '8px 12px', background: 'none', border: 'none',
          cursor: 'pointer', userSelect: 'none',
        }}
      >
        <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.07em' }}>
          {label}
        </span>
        <span style={{ fontSize: 10, color: 'var(--ink-3)', transition: 'transform .15s', transform: open ? 'rotate(180deg)' : 'none' }}>▾</span>
      </button>
      {open && <div style={{ padding: '0 12px 10px' }}>{children}</div>}
    </div>
  );
}

// ── Constants ──────────────────────────────────────────────────────────────

const DIRECTION_META = {
  very_bullish: { label: 'Very Bullish',  color: '#22c55e', bg: 'rgba(34,197,94,.12)',   score: 4, icon: '▲▲', short: 'VBull' },
  mild_bullish: { label: 'Mild Bullish',  color: '#86efac', bg: 'rgba(134,239,172,.12)', score: 3, icon: '▲',  short: 'MBull' },
  neutral:      { label: 'Neutral',       color: '#94a3b8', bg: 'rgba(148,163,184,.10)', score: 2, icon: '—',  short: 'Neut'  },
  mild_bearish: { label: 'Mild Bearish',  color: '#fca5a5', bg: 'rgba(252,165,165,.12)', score: 1, icon: '▼',  short: 'MBear' },
  very_bearish: { label: 'Very Bearish',  color: '#ef4444', bg: 'rgba(239,68,68,.12)',   score: 0, icon: '▼▼', short: 'VBear' },
};
const DIRS_ORDERED = ['very_bullish', 'mild_bullish', 'neutral', 'mild_bearish', 'very_bearish'];

// 5-level quality gradient
function _historyQuality(row) {
  if (row.direction_correct === null || row.direction_correct === undefined) return 'pending';
  if (row.direction_correct === 1) {
    if (row.direction === row.actual_direction) return 'perfect';
    return 'correct';
  }
  const dIdx = DIRS_ORDERED.indexOf(row.direction);
  const aIdx = DIRS_ORDERED.indexOf(row.actual_direction);
  if (dIdx >= 0 && aIdx >= 0 && Math.abs(dIdx - aIdx) <= 1) return 'mild_wrong';
  return 'wrong';
}
const QUALITY_COLORS = {
  perfect:    '#22c55e',
  correct:    '#86efac',
  pending:    '#475569',
  mild_wrong: '#f59e0b',
  wrong:      '#ef4444',
};

const EVENT_TYPE_META = {
  FOMC:                 { label: 'FOMC',        color: '#f59e0b', bg: 'rgba(245,158,11,.12)' },
  RBI_MPC:              { label: 'RBI MPC',      color: '#6366f1', bg: 'rgba(99,102,241,.12)' },
  US_CPI:               { label: 'US CPI',       color: '#ef4444', bg: 'rgba(239,68,68,.10)'  },
  US_NFP:               { label: 'US NFP',       color: '#f97316', bg: 'rgba(249,115,22,.10)' },
  IN_BUDGET:            { label: 'Budget',       color: '#ec4899', bg: 'rgba(236,72,153,.10)' },
  IN_ELECTION:          { label: 'Election',     color: '#8b5cf6', bg: 'rgba(139,92,246,.10)' },
  NIFTY_MONTHLY_EXPIRY: { label: 'Monthly Exp', color: '#64748b', bg: 'rgba(100,116,139,.10)' },
  OTHER:                { label: 'Event',        color: '#64748b', bg: 'rgba(100,116,139,.10)' },
};

const SIGNAL_LABELS = {
  pcr:            'PCR',
  max_pain:       'Max Pain',
  fii_futures:    'FII Futures',
  iv_regime:      'IV Regime',
  technicals:     'Technicals',
  global_markets: 'Global Mkts',
  breadth:        'Breadth',
  vix_trend:      'VIX Trend',
};

// Signal card definitions — each card knows its weight_key for sorting
const SIGNAL_CARD_DEFS = [
  { id: 'pcr',      weight_key: 'pcr',         label: 'PCR (Near)',
    render: s => ({ value: fmt(s.oi_pcr || s.pcr_near, 2), sub: 'Put-Call Ratio',
      bull: (s.oi_pcr || s.pcr_near) != null ? (s.oi_pcr || s.pcr_near) < 0.9 : null }) },
  { id: 'max_pain', weight_key: 'max_pain',    label: 'Max Pain',
    render: s => ({ value: s.max_pain_dist_pct != null ? `${s.max_pain_dist_pct > 0 ? '+' : ''}${fmt(s.max_pain_dist_pct)}%` : '—',
      sub: s.max_pain ? `Pain: ${Math.round(s.max_pain)}` : null,
      bull: s.max_pain_dist_pct != null ? s.max_pain_dist_pct > 0 : null }) },
  { id: 'fii_day',  weight_key: 'fii_futures', label: 'FII Fut Day',
    render: s => ({ value: fmtCr(s.fii_net_fut_cr), sub: 'Net F&O position',
      bull: s.fii_net_fut_cr != null ? s.fii_net_fut_cr > 0 : null }) },
  { id: 'fii_5d',   weight_key: 'fii_futures', label: 'FII Fut 5d',
    render: s => ({ value: fmtCr(s.fii_net_fut_5d_sum), sub: '5-day cumulative',
      bull: s.fii_net_fut_5d_sum != null ? s.fii_net_fut_5d_sum > 0 : null }) },
  { id: 'iv_pct',   weight_key: 'iv_regime',   label: 'IV Percentile',
    render: s => ({ value: s.iv_pct != null ? `${fmt(s.iv_pct, 0)}%` : '—',
      sub: s.iv ? `VIX: ${fmt(s.iv, 1)}%` : null,
      bull: s.iv_pct != null ? s.iv_pct < 30 : null }) },
  { id: 'iv_rv',    weight_key: 'iv_regime',   label: 'IV-RV Spread',
    render: s => ({ value: s.iv_rv != null ? `${s.iv_rv > 0 ? '+' : ''}${fmt(s.iv_rv, 1)}` : '—',
      sub: 'IV minus HV30', bull: s.iv_rv != null ? s.iv_rv < 0 : null }) },
  { id: 'vix',      weight_key: 'vix_trend',   label: 'VIX',
    render: s => ({ value: fmt(s.vix, 1),
      sub: s.vix_chg != null ? `${s.vix_chg > 0 ? '+' : ''}${fmt(s.vix_chg, 2)} chg` : null,
      bull: s.vix != null ? s.vix < 15 : null }) },
  { id: 'breadth',  weight_key: 'breadth',     label: 'Breadth A/D',
    render: s => ({ value: `${s.breadth_adv || '—'} / ${s.breadth_dec || '—'}`,
      sub: 'Advances / Declines',
      bull: (s.breadth_adv || 0) > (s.breadth_dec || 0) }) },
  { id: 'ema20',    weight_key: 'technicals',  label: '20-EMA',
    render: s => ({ value: s.above_ema20 != null ? (s.above_ema20 ? 'Above' : 'Below') : '—',
      sub: s.ema20 ? `EMA: ${fmt(s.ema20, 0)}` : null, bull: s.above_ema20 }) },
  { id: 'trend',    weight_key: 'technicals',  label: 'Weekly Trend',
    render: s => ({ value: s.weekly_trend ? s.weekly_trend.charAt(0).toUpperCase() + s.weekly_trend.slice(1) : '—',
      sub: 'Price structure',
      bull: s.weekly_trend === 'uptrend' ? true : s.weekly_trend === 'downtrend' ? false : null }) },
  { id: 'high52',   weight_key: 'technicals',  label: '52w High Dist',
    render: s => ({ value: s.pct_from_52w_high != null ? `${fmt(s.pct_from_52w_high, 1)}%` : '—',
      sub: s.week52_high ? `High: ${Math.round(s.week52_high)}` : null,
      bull: s.pct_from_52w_high != null ? s.pct_from_52w_high > -3 : null }) },
  { id: 'fii_cash', weight_key: 'fii_futures', label: 'FII Cash',
    render: s => ({ value: fmtCr(s.fii_cash_cr),
      sub: s.dii_cash_cr != null ? `DII: ${fmtCr(s.dii_cash_cr)}` : null,
      bull: s.fii_cash_cr != null ? s.fii_cash_cr > 0 : null }) },
];

// ── Formatters ─────────────────────────────────────────────────────────────

function fmt(v, dec = 1) {
  if (v == null) return '—';
  return Number(v).toFixed(dec);
}
function fmtCr(v) {
  if (v == null) return '—';
  const n = Number(v);
  return `${n >= 0 ? '+' : ''}${Math.round(n).toLocaleString('en-IN')} Cr`;
}
function fmtPnl(v) {
  if (v == null) return '—';
  const n = Number(v);
  return `${n >= 0 ? '+' : ''}₹${Math.abs(n).toLocaleString('en-IN')}`;
}
function fmtDate(s) {
  if (!s) return '—';
  const d = new Date(s);
  return `${d.getDate()} ${d.toLocaleString('en', { month: 'short' })}`;
}

// ── Micro components ───────────────────────────────────────────────────────

function SCard({ label, value, sub, bull }) {
  const col = bull === true ? 'var(--up-field-ink)' : bull === false ? 'var(--down-field-ink)' : 'var(--ink)';
  return (
    <div style={{
      background: 'var(--surface-3)', border: '.5px solid var(--hair-strong)',
      borderRadius: 5, padding: '5px 7px',
    }}>
      <div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 2, textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</div>
      <div style={{ fontSize: 11.5, fontWeight: 700, color: col }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 1 }}>{sub}</div>}
    </div>
  );
}

function SWeightBar({ label, weight }) {
  const w = Math.round((weight || 0) * 100);
  const col = w >= 70 ? '#22c55e' : w >= 40 ? '#f59e0b' : '#94a3b8';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
      <div style={{ width: 96, fontSize: 11.5, color: 'var(--ink-3)', textAlign: 'right', flexShrink: 0 }}>{label}</div>
      <div style={{ flex: 1, height: 4, background: 'var(--surface-3)', borderRadius: 2, overflow: 'hidden' }}>
        <div style={{ width: `${w}%`, height: '100%', background: col, borderRadius: 2 }}/>
      </div>
      <div style={{ width: 24, fontSize: 11.5, color: 'var(--ink-3)', textAlign: 'right', flexShrink: 0 }}>{w}%</div>
    </div>
  );
}

function DirectionGauge({ direction, confidence }) {
  const meta = DIRECTION_META[direction] || DIRECTION_META.neutral;
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ display: 'flex', gap: 3, justifyContent: 'center', marginBottom: 8 }}>
        {DIRS_ORDERED.map(d => {
          const m = DIRECTION_META[d];
          const active = d === direction;
          return (
            <div key={d} style={{
              width: 28, height: 28, borderRadius: 5,
              background: active ? m.color : 'var(--surface-3)',
              border: active ? `2px solid ${m.color}` : '.5px solid var(--hair-strong)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 11, fontWeight: active ? 800 : 400,
              color: active ? '#fff' : 'var(--ink-3)',
            }}>{m.icon}</div>
          );
        })}
      </div>
      <div style={{ fontSize: 17, fontWeight: 800, color: meta.color, marginBottom: 2 }}>{meta.label}</div>
      <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginBottom: 5 }}>Confidence</div>
      <div style={{
        position: 'relative', width: '100%', height: 5,
        background: 'var(--surface-3)', borderRadius: 3, overflow: 'hidden', marginBottom: 3,
      }}>
        <div style={{
          position: 'absolute', left: 0, top: 0, bottom: 0,
          width: `${confidence}%`,
          background: confidence >= 70 ? meta.color : confidence >= 45 ? '#f59e0b' : '#94a3b8',
          borderRadius: 3, transition: 'width .4s ease',
        }}/>
      </div>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)' }}>{confidence}%</div>
    </div>
  );
}

function EventPill({ event, small }) {
  const meta = EVENT_TYPE_META[event.type || event.event_type] || EVENT_TYPE_META.OTHER;
  const daysAway = event.date || event.event_date
    ? Math.ceil((new Date(event.date || event.event_date) - new Date()) / 86400000)
    : null;
  const label = daysAway === 0 ? 'Today' : daysAway === 1 ? 'Tomorrow' : daysAway != null ? `${daysAway}d` : '';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: small ? '2px 5px' : '3px 7px', borderRadius: 4,
      background: meta.bg, border: `.5px solid ${meta.color}40`,
      fontSize: small ? 8.5 : 9, whiteSpace: 'nowrap',
    }}>
      <span style={{ fontWeight: 700, color: meta.color }}>{meta.label}</span>
      {!small && <span style={{ color: 'var(--ink-3)' }}>{event.date || event.event_date}</span>}
      {label && (
        <span style={{
          padding: '0 3px', borderRadius: 2, fontSize: 11,
          background: meta.color, color: '#fff', fontWeight: 700,
        }}>{label}</span>
      )}
    </span>
  );
}

// ── Strategy Info Panel — inline (not modal) ────────────────────────────────

function StrategyInfoPanel({ onClose }) {
  const info = STRATEGY_INFO.weekly_nifty_spread;
  if (!info) return null;

  const SL = ({ children }) => (
    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--ink-3)', marginBottom: 5 }}>
      {children}
    </div>
  );

  return (
    <div style={{
      background: 'var(--surface-2)', border: '.5px solid var(--hair)',
      borderRadius: 6, padding: '12px 14px', marginBottom: 12,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--ink)', marginBottom: 2 }}>
            Strategy 4 — Weekly NIFTY AI Debit Spread
          </div>
          {info.schedule && (
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: 6,
              marginTop: 6, padding: '5px 8px',
              background: 'rgba(59,130,246,.06)', border: '.5px solid rgba(59,130,246,.2)',
              borderRadius: 4, maxWidth: 540,
            }}>
              <span style={{ fontSize: 11, flexShrink: 0 }}>🕓</span>
              <span style={{ fontSize: 11, color: 'var(--ink-2)', lineHeight: 1.55 }}>{info.schedule}</span>
            </div>
          )}
        </div>
        <button onClick={onClose} style={{
          background: 'none', border: 'none', color: 'var(--ink-3)',
          fontSize: 16, cursor: 'pointer', lineHeight: 1, padding: '0 0 0 10px', flexShrink: 0,
        }}>✕</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
        <div>
          <SL>Strategy Principle</SL>
          <div style={{ fontSize: 11.5, color: 'var(--ink)', lineHeight: 1.6, marginBottom: 12 }}>{info.principle}</div>

          <SL>Entry Conditions</SL>
          {info.requiredFilters?.map((f, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 3, alignItems: 'flex-start' }}>
              <span style={{ color: 'var(--up-field-ink)', fontSize: 11, flexShrink: 0, marginTop: 1 }}>✓</span>
              <span style={{ fontSize: 11, color: 'var(--ink-2)', lineHeight: 1.5 }}>{f}</span>
            </div>
          ))}
        </div>
        <div>
          <SL>Signal Scoring</SL>
          {info.confidenceCriteria?.map((c, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 3, alignItems: 'flex-start' }}>
              <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--amber-ink)', flexShrink: 0, marginTop: 2, minWidth: 16 }}>›</span>
              <span style={{ fontSize: 11, color: 'var(--ink-2)', lineHeight: 1.5 }}>{c}</span>
            </div>
          ))}
          {info.actionableThreshold && (
            <div style={{ marginTop: 6, fontSize: 11, color: 'var(--ink-3)', paddingLeft: 8, borderLeft: '2px solid var(--hair-strong)' }}>
              {info.actionableThreshold}
            </div>
          )}
        </div>
      </div>

      {info.researchNotes?.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <SL>Research Notes</SL>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
            {info.researchNotes.map((n, i) => (
              <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 3, alignItems: 'flex-start' }}>
                <span style={{ color: 'var(--ink-3)', fontSize: 11, flexShrink: 0 }}>›</span>
                <span style={{ fontSize: 11, color: 'var(--ink-2)', lineHeight: 1.5 }}>{n}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Events Column (sidebar) ─────────────────────────────────────────────────

function EventsColumn({ events, monthlyExpiries, onRefresh }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm]       = useState({ event_date: '', event_type: 'OTHER', title: '', impact: 'HIGH' });
  const [saving, setSaving]   = useState(false);

  // Merge, deduplicate by (event_date, event_type), sort ascending
  const allEvents = (() => {
    const merged = [
      ...events,
      ...(monthlyExpiries || []).map(d => ({
        id: null, event_date: d, event_type: 'NIFTY_MONTHLY_EXPIRY',
        title: 'NIFTY Monthly Expiry', impact: 'MEDIUM', source: 'auto',
      })),
    ];
    const seen = new Set();
    return merged
      .filter(e => {
        const key = `${e.event_date}||${e.event_type}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .sort((a, b) => (a.event_date || '').localeCompare(b.event_date || ''));
  })();

  const handleAdd = async () => {
    if (!form.event_date || !form.title) return;
    setSaving(true);
    try {
      await addStrategy4Event({ ...form, notes: null });
      setForm({ event_date: '', event_type: 'OTHER', title: '', impact: 'HIGH' });
      setShowAdd(false);
      onRefresh?.();
    } finally { setSaving(false); }
  };

  const iStyle = {
    width: '100%', boxSizing: 'border-box', padding: '3px 5px', fontSize: 11,
    background: 'var(--paper)', border: '.5px solid var(--hair)',
    borderRadius: 3, color: 'var(--ink)',
  };
  const selStyle = {
    width: '100%', padding: '2px 3px', fontSize: 11.5,
    background: 'var(--paper)', border: '.5px solid var(--hair)',
    borderRadius: 3, color: 'var(--ink)',
  };

  return (
    <div style={{
      background: 'var(--surface-2)', border: '.5px solid var(--hair)',
      borderRadius: 7, padding: '10px 12px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.05em' }}>
          Upcoming Events
        </span>
        <button onClick={() => setShowAdd(s => !s)} style={{
          fontSize: 11, padding: '1px 6px', borderRadius: 3, cursor: 'pointer',
          background: 'transparent', border: '.5px solid var(--hair-strong)', color: 'var(--ink-3)',
        }}>{showAdd ? '✕' : '+ Add'}</button>
      </div>

      {allEvents.length === 0 ? (
        <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>No events in next 30 days.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          {allEvents.map((e, i) => {
            const meta = EVENT_TYPE_META[e.event_type] || EVENT_TYPE_META.OTHER;
            const d = new Date(e.event_date);
            const dayName = d.toLocaleString('en', { weekday: 'short' });
            const datePart = `${d.getDate()} ${d.toLocaleString('en', { month: 'short' })}`;
            const daysAway = Math.ceil((d - new Date()) / 86400000);
            const dayBadge = daysAway === 0 ? 'Today' : daysAway === 1 ? 'Tomorrow' : `${daysAway}d`;
            // Brief description: notes first, then title truncated
            const brief = (e.notes || e.title || '').slice(0, 42);
            return (
              <div key={i} style={{
                paddingBottom: 6,
                borderBottom: i < allEvents.length - 1 ? '.5px solid var(--hair-strong)' : 'none',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2 }}>
                  <span style={{ fontSize: 11.5, color: 'var(--ink-3)', minWidth: 52 }}>{dayName}, {datePart}</span>
                  <span style={{ fontSize: 11.5, fontWeight: 700, color: meta.color }}>{meta.label}</span>
                  <span style={{
                    fontSize: 11, padding: '0 4px', borderRadius: 2,
                    background: meta.color, color: '#fff', fontWeight: 700,
                  }}>{dayBadge}</span>
                  {e.source !== 'auto' && e.id && (
                    <button
                      onClick={() => deleteStrategy4Event(e.id).then(onRefresh)}
                      style={{ fontSize: 11, color: 'var(--ink-3)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginLeft: 'auto', opacity: 0.5 }}
                      title="Remove">✕</button>
                  )}
                </div>
                {brief && (
                  <div style={{ fontSize: 11.5, color: 'var(--ink-3)', lineHeight: 1.3, paddingLeft: 1 }}>
                    {brief}{brief.length < (e.notes || e.title || '').length ? '…' : ''}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showAdd && (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 5 }}>
          <div>
            <div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 2 }}>Date</div>
            <input type="date" value={form.event_date}
              onChange={e => setForm(f => ({ ...f, event_date: e.target.value }))}
              style={iStyle}/>
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 2 }}>Title</div>
            <input type="text" value={form.title} placeholder="Event name"
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              style={iStyle}/>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 5 }}>
            <div>
              <div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 2 }}>Type</div>
              <select value={form.event_type} onChange={e => setForm(f => ({ ...f, event_type: e.target.value }))} style={selStyle}>
                {Object.entries(EVENT_TYPE_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 2 }}>Impact</div>
              <select value={form.impact} onChange={e => setForm(f => ({ ...f, impact: e.target.value }))} style={selStyle}>
                {['HIGH','MEDIUM','LOW'].map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
          </div>
          <button onClick={handleAdd} disabled={saving || !form.event_date || !form.title}
            style={{ padding: '4px 0', fontSize: 11, fontWeight: 600, borderRadius: 3,
              cursor: 'pointer', background: 'var(--amber-ink)', border: 'none', color: '#fff',
              opacity: saving ? 0.6 : 1 }}>
            {saving ? '…' : 'Add Event'}
          </button>
        </div>
      )}
    </div>
  );
}

// ── History Chart ──────────────────────────────────────────────────────────

function HistoryChart({ history }) {
  if (!history?.length) return (
    <div style={{ textAlign: 'center', padding: '14px 0', color: 'var(--ink-3)', fontSize: 11.5 }}>
      No prediction history yet — predictions appear here each week.
    </div>
  );

  const W = 720, H = 90, PL = 8, PR = 8, PT = 8, PB = 20;
  const cW = W - PL - PR, cH = H - PT - PB, n = history.length;
  const bw = Math.max(3, Math.min(12, Math.floor(cW / n) - 2));
  const xOf = i => PL + (i + 0.5) * (cW / n);
  const midY = PT + cH / 2;

  return (
    <div style={{ overflowX: 'auto' }}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: H, minWidth: 300, display: 'block' }}>
        <line x1={PL} x2={W - PR} y1={midY} y2={midY} stroke="var(--hair-strong)" strokeWidth=".5"/>
        {history.map((row, i) => {
          const meta    = DIRECTION_META[row.direction] || DIRECTION_META.neutral;
          const quality = _historyQuality(row);
          const qColor  = QUALITY_COLORS[quality];
          const cx      = xOf(i);
          const conf    = (row.confidence || 50) / 100;
          const score   = (meta.score - 2) / 2;
          const maxBarH = (cH / 2) - 3;
          const barH    = Math.max(2, conf * maxBarH);
          const barY    = score >= 0 ? midY - barH : midY;
          return (
            <rect key={row.week_start} x={cx - bw / 2} y={barY} width={bw} height={barH}
              fill={qColor} opacity={quality === 'pending' ? 0.35 : 0.85} rx={1}/>
          );
        })}
        {history.map((row, i) => {
          if (i % 4 !== 0) return null;
          const d = new Date(row.week_start);
          return (
            <text key={row.week_start} x={xOf(i)} y={H - 3}
              textAnchor="middle" fontSize={11} fill="var(--ink-3)">
              {d.getDate()}/{d.getMonth() + 1}
            </text>
          );
        })}
      </svg>
      <div style={{ display: 'flex', gap: 12, fontSize: 11.5, color: 'var(--ink-3)', marginTop: 4, flexWrap: 'wrap' }}>
        {[['perfect','Perfect (exact)'],['correct','Correct'],['pending','Pending'],['mild_wrong','Mild miss'],['wrong','Wrong']].map(([q, l]) => (
          <span key={q} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <span style={{ width: 8, height: 8, background: QUALITY_COLORS[q], borderRadius: 2, display: 'inline-block', opacity: q === 'pending' ? 0.35 : 0.85 }}/>
            {l}
          </span>
        ))}
        <span style={{ marginLeft: 4, opacity: 0.65 }}>Bar height = confidence</span>
      </div>
    </div>
  );
}

// ── History Table ──────────────────────────────────────────────────────────

function HistoryTable({ history }) {
  const [open, setOpen] = useState(false);
  const rows = [...(history || [])].reverse().slice(0, 26);
  if (!rows.length) return null;
  return (
    <div style={{ marginTop: 8 }}>
      <button onClick={() => setOpen(o => !o)} style={{
        fontSize: 11.5, padding: '4px 10px', borderRadius: 4, cursor: 'pointer',
        background: 'var(--surface-3)', border: '.5px solid var(--hair-strong)', color: 'var(--ink-3)',
      }}>
        {open ? '▲ Hide Table' : `▼ Show Details (${rows.length} weeks)`}
      </button>
      {open && (
        <div style={{ overflowX: 'auto', marginTop: 8 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11.5 }}>
            <thead>
              <tr style={{ color: 'var(--ink-3)', borderBottom: '.5px solid var(--hair-strong)' }}>
                {['Week','Predicted','Conf','Move %','Actual Dir','Result','P&L'].map((h, i) => (
                  <th key={h} style={{ padding: '3px 8px', fontWeight: 600, textAlign: i >= 2 ? 'right' : 'left', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(row => {
                const pm = DIRECTION_META[row.direction]        || DIRECTION_META.neutral;
                const am = DIRECTION_META[row.actual_direction] || null;
                const hasOut = row.direction_correct != null;
                const ok     = row.direction_correct === 1;
                const quality = _historyQuality(row);
                return (
                  <tr key={row.week_start} style={{ borderBottom: '.5px solid var(--hair)', opacity: hasOut ? 1 : 0.55 }}>
                    <td style={{ padding: '4px 8px', color: 'var(--ink-3)', whiteSpace: 'nowrap' }}>{fmtDate(row.week_start)}</td>
                    <td style={{ padding: '4px 8px', color: pm.color, fontWeight: 600 }}>{pm.short}</td>
                    <td style={{ padding: '4px 8px', textAlign: 'right', color: 'var(--ink-2)' }}>{row.confidence}%</td>
                    <td style={{ padding: '4px 8px', textAlign: 'right',
                      color: row.actual_move_pct > 0 ? 'var(--up-field-ink)' : row.actual_move_pct < 0 ? 'var(--down-field-ink)' : 'var(--ink-3)' }}>
                      {row.actual_move_pct != null ? `${row.actual_move_pct > 0 ? '+' : ''}${fmt(row.actual_move_pct, 2)}%` : '—'}
                    </td>
                    <td style={{ padding: '4px 8px', color: am ? am.color : 'var(--ink-3)' }}>{am ? am.short : '—'}</td>
                    <td style={{ padding: '4px 8px', textAlign: 'right' }}>
                      {hasOut
                        ? <span style={{ fontWeight: 700, color: QUALITY_COLORS[quality] }}>{quality === 'perfect' ? '★' : ok ? '✓' : '✗'}</span>
                        : <span style={{ color: 'var(--ink-3)' }}>—</span>}
                    </td>
                    <td style={{ padding: '4px 8px', textAlign: 'right',
                      color: row.total_pnl > 0 ? 'var(--up-field-ink)' : row.total_pnl < 0 ? 'var(--down-field-ink)' : 'var(--ink-3)' }}>
                      {row.total_pnl != null ? fmtPnl(row.total_pnl) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Record Outcome Form ────────────────────────────────────────────────────

function RecordOutcomeForm({ weekStart, onRecorded }) {
  const [open, setOpen]       = useState(false);
  const [nOpen, setNOpen]     = useState('');
  const [nClose, setNClose]   = useState('');
  const [primPnl, setPrimPnl] = useState('');
  const [hedgePnl, setHedge]  = useState('');
  const [notes, setNotes]     = useState('');
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);

  const handleSave = async () => {
    if (!nOpen || !nClose) return;
    setSaving(true);
    try {
      await postStrategy4Outcome({
        week_start:  weekStart,
        nifty_open:  parseFloat(nOpen),
        nifty_close: parseFloat(nClose),
        primary_pnl: primPnl  ? parseFloat(primPnl)  : null,
        hedge_pnl:   hedgePnl ? parseFloat(hedgePnl) : null,
        notes:       notes || null,
      });
      setSaved(true);
      onRecorded?.();
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  if (saved) return <div style={{ fontSize: 11, color: 'var(--up-field-ink)', padding: '6px 0' }}>✓ Outcome recorded</div>;

  return (
    <div style={{ marginTop: 10, paddingTop: 10, borderTop: '.5px solid var(--hair-strong)' }}>
      <button onClick={() => setOpen(o => !o)} style={{
        fontSize: 11.5, padding: '4px 10px', borderRadius: 4, cursor: 'pointer',
        background: 'var(--surface-3)', border: '.5px solid var(--hair-strong)', color: 'var(--ink-3)',
      }}>
        {open ? '▲ Hide' : '▼ Record Outcome for this week'}
      </button>
      {open && (
        <div style={{ marginTop: 8, padding: '10px 12px', background: 'var(--surface-3)', border: '.5px solid var(--hair-strong)', borderRadius: 6 }}>
          <div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 8 }}>Record NIFTY results for week {weekStart}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 7, marginBottom: 8 }}>
            {[
              ['Mon Open', nOpen, setNOpen],
              ['Tue Close', nClose, setNClose],
              ['CE Spread P&L (₹)', primPnl, setPrimPnl],
              ['PE Hedge P&L (₹)', hedgePnl, setHedge],
            ].map(([label, val, setter]) => (
              <div key={label}>
                <div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 2 }}>{label}</div>
                <input type="number" value={val} onChange={e => setter(e.target.value)}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '4px 6px', fontSize: 11.5,
                    background: 'var(--surface-2)', border: '.5px solid var(--hair-strong)', borderRadius: 4, color: 'var(--ink)' }}/>
              </div>
            ))}
          </div>
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 2 }}>Notes</div>
            <input type="text" value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="e.g. Closed early, gap-up Monday..."
              style={{ width: '100%', boxSizing: 'border-box', padding: '4px 6px', fontSize: 11.5,
                background: 'var(--surface-2)', border: '.5px solid var(--hair-strong)', borderRadius: 4, color: 'var(--ink)' }}/>
          </div>
          <button onClick={handleSave} disabled={saving || !nOpen || !nClose}
            style={{ padding: '5px 16px', fontSize: 11.5, fontWeight: 600, borderRadius: 4,
              cursor: 'pointer', background: 'var(--amber-ink)', border: 'none', color: '#fff',
              opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Saving…' : 'Save Outcome'}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────

export default function Strategy4Section({ showHeader = true }) {
  const [collapsed,    setCollapsed]  = useState(false);
  const [showAbout,    setShowAbout]  = useState(false);
  const [current,      setCurrent]    = useState(null);
  const [history,      setHistory]    = useState([]);
  const [summary,      setSummary]    = useState(null);
  const [generating,   setGenerating] = useState(false);
  const [loading,      setLoading]    = useState(true);
  const [error,        setError]      = useState(null);
  const [upcomingEvents,  setUpcoming]  = useState([]);
  const [monthlyExpiries, setMonthly]   = useState([]);

  const loadData = useCallback(async () => {
    try {
      const [cur, hist, evts] = await Promise.all([
        getStrategy4Current(),
        getStrategy4History(52),
        getStrategy4Events(30),
      ]);
      setCurrent(cur);
      setHistory(hist.history || []);
      setSummary(hist.summary || null);
      setUpcoming(evts.events || []);
      setMonthly(evts.monthly_expiry_dates || []);
    } catch (e) {
      setError('Failed to load Strategy 4 data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleGenerate = async (force = false) => {
    setGenerating(true);
    setError(null);
    try {
      await generateStrategy4Prediction(force);
      await loadData();
    } catch (e) {
      setError(e.message || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const pred      = current?.prediction;
  const signals   = pred?.signals        || {};
  const weights   = pred?.signal_weights || {};
  const backdrop  = pred?.backdrop       || null;
  const isBuilding = pred?.status === 'building';

  // Sort signal cards by their weight descending
  const sortedCards = [...SIGNAL_CARD_DEFS].sort(
    (a, b) => (weights[b.weight_key] || 0) - (weights[a.weight_key] || 0)
  );

  // If pred exists but backdrop is null, force=true clears the stale cache
  const needsBackdropRefresh = pred && !backdrop;

  // Strike suggestions
  const spot    = signals.nifty_close || signals.oi_spot;
  const isBull  = pred?.direction?.includes('bull');
  const isBear  = pred?.direction?.includes('bear');
  const ceLow   = spot ? Math.round((spot + 200) / 50) * 50 : null;
  const ceHigh  = ceLow  ? ceLow  + 200 : null;
  const peHigh  = spot ? Math.round((spot - 200) / 50) * 50 : null;
  const peLow   = peHigh ? peHigh - 200 : null;

  const globalMkts = signals.global_markets || {};
  const GLOBAL_LABELS = { SPY: 'S&P 500', QQQ: 'NASDAQ', '^N225': 'Nikkei', '^HSI': 'Hang Seng', '^DJI': 'Dow Jones' };

  // Shared panel style
  const panel = (extra = {}) => ({
    background: 'var(--surface-2)', border: '.5px solid var(--hair)',
    borderRadius: 7, padding: '10px 12px', ...extra,
  });
  const panelHdr = {
    fontSize: 11.5, fontWeight: 700, color: 'var(--ink-3)',
    textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 8,
  };

  // When embedded in StrategyCard (showHeader=false), body always visible
  const isCollapsed = showHeader ? collapsed : false;

  // Action buttons — reused in both header and headerless controls row
  const actionButtons = (
    <div style={{ display: 'flex', gap: 5, flexShrink: 0, alignItems: 'center' }}>
      <button onClick={() => setShowAbout(s => !s)} style={{
        fontSize: 11, padding: '2px 7px', borderRadius: 3, cursor: 'pointer',
        background: showAbout ? 'rgba(59,130,246,.12)' : 'transparent',
        border: showAbout ? '.5px solid rgba(59,130,246,.3)' : '.5px solid var(--hair-strong)',
        color: 'var(--ink-3)', fontWeight: 600,
      }}>ⓘ About</button>
      {pred && (
        <button onClick={() => handleGenerate(true)} disabled={generating}
          title="Force regenerate (re-runs backdrop + prediction)"
          style={{ fontSize: 11, padding: '3px 8px', borderRadius: 4, cursor: 'pointer',
            background: 'none', border: '.5px solid var(--hair-strong)', color: 'var(--ink-3)',
            opacity: generating ? 0.5 : 1 }}>
          ↻ Refresh
        </button>
      )}
      <button
        onClick={() => handleGenerate(needsBackdropRefresh ? true : false)}
        disabled={generating || loading}
        style={{ fontSize: 11.5, padding: '4px 12px', borderRadius: 4, cursor: 'pointer', fontWeight: 600,
          background: generating ? 'var(--surface-3)' : 'var(--amber-ink)',
          border: 'none', color: generating ? 'var(--ink-3)' : '#fff', minWidth: 88 }}>
        {generating ? '⏳ Generating…' : needsBackdropRefresh ? '↻ Re-run' : pred ? '✓ Generated' : '▶ Generate'}
      </button>
    </div>
  );

  // Embedded in StrategyCard → publish action buttons to the shared header row.
  const setHeaderActions = useContext(HeaderActionsContext);
  useEffect(() => {
    if (showHeader) return;
    setHeaderActions(actionButtons);
    return () => setHeaderActions(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showHeader, showAbout, generating, loading, pred, needsBackdropRefresh]);

  return (
    <div style={{
      background: 'var(--surface)',
      border: showHeader ? '.5px solid var(--hair)' : 'none',
      borderRadius: showHeader ? 8 : 0,
      padding: showHeader ? '12px 14px' : '0',
      marginTop: showHeader ? 10 : 0,
    }}>
      {/* ── Header — only when showHeader=true ── */}
      {showHeader && (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button onClick={() => setCollapsed(c => !c)} style={{
          background: 'none', border: 'none', color: 'var(--ink-3)',
          cursor: 'pointer', fontSize: 11, padding: '0 2px', lineHeight: 1,
        }}>{collapsed ? '▶' : '▼'}</button>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--ink)' }}>
              Strategy 4 — Weekly NIFTY AI Debit Spread
            </span>
            {isBuilding && (
              <span style={{
                fontSize: 11.5, padding: '1px 7px', borderRadius: 3,
                background: 'rgba(245,158,11,.1)', border: '.5px solid rgba(245,158,11,.3)', color: '#f59e0b',
              }}>⚠ Calibrating — {summary?.completed_weeks || 0}/8 weeks</span>
            )}
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 1 }}>
            CE spread (current week Tue) + PE hedge 2:5 sizing · Two-step AI: Sonnet backdrop → Opus prediction
          </div>
        </div>
        {actionButtons}
      </div>
      )}

      {/* Embedded: action buttons published to the StrategyCard header (see useEffect).
          Keep the calibrating badge here in the body. */}
      {!showHeader && isBuilding && (
        <div style={{
          display: 'flex', alignItems: 'center',
          padding: '7px 12px', borderBottom: '.5px solid var(--hair)',
        }}>
          <span style={{ fontSize: 11, padding: '1px 7px', borderRadius: 3,
            background: 'rgba(245,158,11,.1)', border: '.5px solid rgba(245,158,11,.3)', color: '#f59e0b' }}>
            ⚠ Calibrating — {summary?.completed_weeks || 0}/8 weeks
          </span>
        </div>
      )}

      {/* ── Body ── */}
      {!isCollapsed && (
        <div style={{ marginTop: showHeader ? 12 : 0 }}>

          {/* About panel — inline, above sections */}
          {showAbout && <StrategyInfoPanel onClose={() => setShowAbout(false)}/>}

          {error && (
            <div style={{ marginBottom: 8, padding: '6px 10px', borderRadius: 5,
              background: 'rgba(239,68,68,.08)', border: '.5px solid rgba(239,68,68,.25)',
              fontSize: 11, color: 'var(--down-field-ink)' }}>
              {error}
            </div>
          )}

          {loading ? (
            <div style={{ textAlign: 'center', padding: '20px', color: 'var(--ink-3)', fontSize: 11.5 }}>Loading…</div>
          ) : (
            <>
              {/* ── Section 1: On the plate now (open by default) ── */}
              <CardSection label="On the plate now" defaultOpen>
                {/* ROW 1: Prediction | Setup | Events */}
                <div style={{ display: 'grid', gridTemplateColumns: '190px 1fr 220px', gap: 10, marginBottom: 10, marginTop: 10 }}>

                  {/* Prediction */}
                  <div style={panel()}>
                    <div style={panelHdr}>This Week's Prediction</div>
                    {pred ? (
                      <>
                        <DirectionGauge direction={pred.direction} confidence={pred.confidence}/>
                        <div style={{ fontSize: 11, color: 'var(--ink-3)', textAlign: 'center', marginTop: 6 }}>
                          {current?.nifty_expiry && `Expiry: ${current.nifty_expiry}`}
                        </div>
                        {pred.generated_at && (
                          <div style={{ fontSize: 11, color: 'var(--ink-3)', textAlign: 'center', marginTop: 3, opacity: 0.65 }}>
                            {pred.cached ? 'Cached' : 'Generated'}{' '}
                            {new Date(pred.generated_at.replace(' ', 'T') + 'Z').toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })}
                          </div>
                        )}
                      </>
                    ) : (
                      <div style={{ color: 'var(--ink-3)', fontSize: 11, textAlign: 'center', padding: '20px 0' }}>
                        No prediction yet.<br/>
                        <span style={{ fontSize: 11, opacity: 0.7 }}>Click Generate →</span>
                      </div>
                    )}
                  </div>

                  {/* Setup */}
                  <div style={panel()}>
                    {pred ? (
                      <>
                        <div style={panelHdr}>
                          Recommended Setup {pred.direction === 'neutral' ? '— Neutral: no primary trade' : '(Indicative)'}
                        </div>
                        {(isBull || isBear) && spot ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                            <div>
                              <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginBottom: 4 }}>
                                <strong>PRIMARY</strong> — {isBull ? 'CE' : 'PE'} Debit Spread · Current week (Tue expiry) · Full size
                              </div>
                              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                                <div style={{
                                  padding: '5px 10px', borderRadius: 5, fontSize: 11, fontWeight: 700,
                                  background: isBull ? 'rgba(34,197,94,.1)' : 'rgba(239,68,68,.1)',
                                  border: `.5px solid ${isBull ? 'rgba(34,197,94,.3)' : 'rgba(239,68,68,.3)'}`,
                                  color: isBull ? 'var(--up-field-ink)' : 'var(--down-field-ink)',
                                }}>Buy {isBull ? ceLow : peHigh} {isBull ? 'CE' : 'PE'}</div>
                                <span style={{ color: 'var(--ink-3)', fontSize: 11 }}>→</span>
                                <div style={{
                                  padding: '5px 10px', borderRadius: 5, fontSize: 11, fontWeight: 700,
                                  background: 'var(--surface-3)', border: '.5px solid var(--hair-strong)', color: 'var(--ink-2)',
                                }}>Sell {isBull ? ceHigh : peLow} {isBull ? 'CE' : 'PE'}</div>
                                <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>~200pt wide · NIFTY ≈ {Math.round(spot)}</span>
                              </div>
                            </div>
                            <div>
                              <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginBottom: 4 }}>
                                <strong>HEDGE</strong> — {isBull ? 'PE' : 'CE'} Debit Spread · Next week (Tue expiry) · 2:5 sizing
                              </div>
                              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                                <div style={{
                                  padding: '5px 10px', borderRadius: 5, fontSize: 11, fontWeight: 700,
                                  background: isBull ? 'rgba(239,68,68,.08)' : 'rgba(34,197,94,.08)',
                                  border: `.5px solid ${isBull ? 'rgba(239,68,68,.2)' : 'rgba(34,197,94,.2)'}`,
                                  color: isBull ? 'var(--down-field-ink)' : 'var(--up-field-ink)',
                                }}>Buy {isBull ? peHigh : ceLow} {isBull ? 'PE' : 'CE'}</div>
                                <span style={{ color: 'var(--ink-3)', fontSize: 11 }}>→</span>
                                <div style={{
                                  padding: '5px 10px', borderRadius: 5, fontSize: 11, fontWeight: 700,
                                  background: 'var(--surface-3)', border: '.5px solid var(--hair-strong)', color: 'var(--ink-2)',
                                }}>Sell {isBull ? peLow : ceHigh} {isBull ? 'PE' : 'CE'}</div>
                                <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>2 lots per 5 primary · ~13 DTE</span>
                              </div>
                            </div>
                            {pred.trade_notes && (
                              <div style={{
                                padding: '6px 9px', borderRadius: 5, fontSize: 11.5,
                                background: 'rgba(99,102,241,.07)', color: 'var(--ink)', fontStyle: 'italic',
                              }}>💡 {pred.trade_notes}</div>
                            )}
                            <div style={{ fontSize: 11.5, color: 'var(--ink-3)', opacity: 0.75 }}>
                              Strikes nearest 50pt. Verify against live chain. Entry: Wed morning before 10:30 IST.
                            </div>
                          </div>
                        ) : pred.direction === 'neutral' ? (
                          <div style={{ fontSize: 11, color: 'var(--ink-3)', padding: '8px 0' }}>
                            No directional trade this week. Consider iron condor or stay flat.
                            {pred.trade_notes && <div style={{ marginTop: 6, fontStyle: 'italic', color: 'var(--ink-2)' }}>💡 {pred.trade_notes}</div>}
                          </div>
                        ) : (
                          <div style={{ fontSize: 11, color: 'var(--ink-3)', padding: '8px 0' }}>
                            Spot data unavailable — cannot compute indicative strikes.
                          </div>
                        )}
                      </>
                    ) : (
                      <div style={{ color: 'var(--ink-3)', fontSize: 11, padding: '20px 0' }}>
                        <div style={{ fontWeight: 600, color: 'var(--ink)', marginBottom: 5 }}>Click Generate to run two-step AI analysis</div>
                        <div style={{ fontSize: 11, opacity: 0.8, lineHeight: 1.6 }}>
                          Step 1: Sonnet reads recent news → macro backdrop<br/>
                          Step 2: Opus makes directional call (~20–30 s)
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Events column */}
                  <EventsColumn events={upcomingEvents} monthlyExpiries={monthlyExpiries} onRefresh={loadData}/>
                </div>
              </CardSection>

              {/* ── Section 2: History (collapsed) ── */}
              <CardSection label="History">
                <div style={{ marginTop: 10 }}>
                  {pred ? (
                    <div style={{ ...panel(), marginBottom: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                        <span style={panelHdr}>Prediction History</span>
                        {summary?.completed_weeks > 0 && (
                          <div style={{ display: 'flex', gap: 14, fontSize: 11 }}>
                            <span style={{ color: 'var(--ink-3)' }}>
                              Accuracy:{' '}
                              <strong style={{ color: (summary.accuracy_pct || 0) >= 60 ? 'var(--up-field-ink)' : 'var(--down-field-ink)' }}>
                                {summary.accuracy_pct}%
                              </strong>{' '}
                              ({summary.correct_weeks}/{summary.completed_weeks} wks)
                            </span>
                            {summary.total_pnl != null && (
                              <span style={{ color: 'var(--ink-3)' }}>
                                P&L:{' '}
                                <strong style={{ color: summary.total_pnl >= 0 ? 'var(--up-field-ink)' : 'var(--down-field-ink)' }}>
                                  {fmtPnl(summary.total_pnl)}
                                </strong>
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <HistoryChart history={history}/>
                      <HistoryTable history={history}/>
                    </div>
                  ) : (
                    <div style={{ fontSize: 11, color: 'var(--ink-3)', padding: '8px 0' }}>No prediction history yet.</div>
                  )}
                  {pred && <RecordOutcomeForm weekStart={current.week_start} onRecorded={loadData}/>}
                </div>
              </CardSection>

              {/* ── Section 3: Reasoning (collapsed) ── */}
              <CardSection label="Reasoning">
                <div style={{ marginTop: 10 }}>
                  {pred ? (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                      {/* Reasoning */}
                      {pred.reasoning?.length > 0 && (
                        <div style={panel()}>
                          <div style={panelHdr}>AI Reasoning (Opus)</div>
                          <ul style={{ margin: 0, paddingLeft: 14 }}>
                            {pred.reasoning.map((pt, i) => (
                              <li key={i} style={{ fontSize: 11.5, color: 'var(--ink)', marginBottom: 5, lineHeight: 1.5 }}>{pt}</li>
                            ))}
                          </ul>
                          {signals.events_this_week?.length > 0 && (
                            <div style={{ display: 'flex', gap: 4, marginTop: 7, flexWrap: 'wrap' }}>
                              {signals.events_this_week.map((e, i) => <EventPill key={i} event={e} small/>)}
                              {signals.is_monthly_expiry_week && (
                                <EventPill event={{ event_type: 'NIFTY_MONTHLY_EXPIRY', event_date: '' }} small/>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Macro Backdrop */}
                      <div style={panel()}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                          <div style={panelHdr}>Macro Backdrop (Sonnet)</div>
                          {backdrop?.overall_sentiment && (
                            <span style={{
                              fontSize: 11.5, padding: '1px 6px', borderRadius: 3, fontWeight: 700, marginBottom: 8,
                              background: backdrop.overall_sentiment === 'risk_on' ? 'rgba(34,197,94,.1)'
                                : backdrop.overall_sentiment === 'risk_off' ? 'rgba(239,68,68,.1)'
                                : 'rgba(148,163,184,.1)',
                              color: backdrop.overall_sentiment === 'risk_on' ? 'var(--up-field-ink)'
                                : backdrop.overall_sentiment === 'risk_off' ? 'var(--down-field-ink)'
                                : 'var(--ink-3)',
                              display: 'inline-flex', alignItems: 'center', gap: 4,
                            }}>
                              {backdrop.overall_sentiment === 'risk_on' ? 'Risk-On'
                                : backdrop.overall_sentiment === 'risk_off' ? 'Risk-Off'
                                : backdrop.overall_sentiment.replace(/_/g, '-')}
                              {backdrop.sentiment_strength && (
                                <span style={{ fontSize: 11, fontWeight: 500, opacity: 0.75 }}>
                                  {backdrop.sentiment_strength}
                                </span>
                              )}
                            </span>
                          )}
                          {backdrop?.confidence_modifier != null && backdrop.confidence_modifier !== 0 && (
                            <span
                              title={`Confidence adjustment from macro backdrop: ${backdrop.confidence_modifier > 0 ? 'positive macro boosts' : 'high uncertainty reduces'} AI prediction confidence by ${Math.abs(backdrop.confidence_modifier)} points`}
                              style={{
                                fontSize: 11, padding: '1px 5px', borderRadius: 3, marginBottom: 8, cursor: 'help',
                                background: 'var(--surface-3)', color: backdrop.confidence_modifier > 0 ? 'var(--up-field-ink)' : 'var(--down-field-ink)',
                              }}>
                              {backdrop.confidence_modifier > 0 ? '+' : ''}{backdrop.confidence_modifier} conf. adj.
                            </span>
                          )}
                        </div>

                        {!backdrop ? (
                          <div style={{ fontSize: 11.5, color: 'var(--ink-3)', lineHeight: 1.6 }}>
                            Backdrop not generated yet.{' '}
                            <button onClick={() => handleGenerate(true)} disabled={generating}
                              style={{ fontSize: 11, padding: '1px 7px', borderRadius: 3, cursor: 'pointer',
                                background: 'var(--amber-ink)', border: 'none', color: '#fff',
                                opacity: generating ? 0.5 : 1 }}>
                              {generating ? '…' : '↻ Re-run now'}
                            </button>
                            <div style={{ marginTop: 4, fontSize: 11.5, opacity: 0.7 }}>
                              Backdrop requires the full two-step pipeline (force refresh).
                            </div>
                          </div>
                        ) : backdrop.error ? (
                          <div style={{ fontSize: 11.5, color: '#f59e0b' }}>
                            ⚠ Backdrop unavailable — {backdrop.themes?.[0] || backdrop.error}
                          </div>
                        ) : (
                          <div>
                            <ul style={{ margin: '0 0 4px', paddingLeft: 14 }}>
                              {(backdrop.themes || []).map((t, i) => (
                                <li key={i} style={{ fontSize: 11.5, color: 'var(--ink)', marginBottom: 4, lineHeight: 1.45 }}>{t}</li>
                              ))}
                              {backdrop.india_specific && (
                                <li style={{ fontSize: 11.5, color: 'var(--ink-2)', marginBottom: 4, lineHeight: 1.45 }}>
                                  🇮🇳 {backdrop.india_specific}
                                </li>
                              )}
                              {backdrop.unknowns && (
                                <li style={{ fontSize: 11.5, color: '#f59e0b', marginBottom: 4, lineHeight: 1.45, fontStyle: 'italic' }}>
                                  ❓ {backdrop.unknowns}
                                </li>
                              )}
                            </ul>
                            {backdrop.news_count > 0 && (
                              <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 3, opacity: 0.65 }}>
                                Based on {backdrop.news_count} HIGH/MEDIUM news items
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div style={{ fontSize: 11, color: 'var(--ink-3)', padding: '8px 0' }}>Generate a prediction to see reasoning.</div>
                  )}
                </div>
              </CardSection>

              {/* ── Section 4: Signal Weights (collapsed) ── */}
              <CardSection label="Signal weights">
                <div style={{ marginTop: 10 }}>
                  {pred ? (
                    <div style={panel()}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                        <span style={panelHdr}>Signals &amp; Weights</span>
                        {signals.snapshot_date && (
                          <span style={{ fontSize: 11, color: 'var(--ink-3)', opacity: 0.65 }}>as of {signals.snapshot_date}</span>
                        )}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '290px 1fr', gap: 14, alignItems: 'start' }}>
                        {/* Left: weight bars */}
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 7 }}>
                            AI Signal Weights
                          </div>
                          {Object.entries(weights)
                            .sort((a, b) => b[1] - a[1])
                            .map(([k, v]) => (
                              <SWeightBar key={k} label={SIGNAL_LABELS[k] || k} weight={v}/>
                            ))
                          }
                        </div>
                        {/* Right: signal cards sorted by weight */}
                        <div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 5, marginBottom: 6 }}>
                            {sortedCards.map(def => {
                              const { value, sub, bull } = def.render(signals);
                              return <SCard key={def.id} label={def.label} value={value} sub={sub} bull={bull}/>;
                            })}
                          </div>
                          {Object.keys(globalMkts).length > 0 && (
                            <div>
                              <div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.04em' }}>
                                Global Markets
                              </div>
                              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                {Object.entries(globalMkts).map(([sym, d]) => {
                                  const chg = d.chg_pct;
                                  const col = chg > 0 ? 'var(--up-field-ink)' : chg < 0 ? 'var(--down-field-ink)' : 'var(--ink-3)';
                                  return (
                                    <div key={sym} style={{
                                      display: 'flex', alignItems: 'center', gap: 5,
                                      padding: '3px 7px', borderRadius: 4,
                                      background: 'var(--surface-3)', border: '.5px solid var(--hair-strong)',
                                    }}>
                                      <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>{GLOBAL_LABELS[sym] || sym}</span>
                                      <span style={{ fontSize: 11.5, fontWeight: 700, color: col }}>
                                        {chg != null ? `${chg > 0 ? '+' : ''}${chg.toFixed(2)}%` : '—'}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div style={{ fontSize: 11, color: 'var(--ink-3)', padding: '8px 0' }}>Generate a prediction to see signal weights.</div>
                  )}
                </div>
              </CardSection>
            </>
          )}
        </div>
      )}
    </div>
  );
}
